import { createListenerMiddleware, isAnyOf, type ListenerEffectAPI } from '@reduxjs/toolkit';

import { baseApi } from '@/shared/api/baseApi';
import { attemptsApi, contentApi } from '@/shared/api/endpoints';
import { errorCode, errorStatus, isTransientError } from '@/shared/api/errors';
import { selectSessionStatus, sessionStarted, type WithSession } from '@/shared/api/sessionSlice';
import type { AppDispatch } from '@/shared/lib/hooks';

import {
  answerQueued,
  flushRequested,
  positionQueued,
  selectNextItem,
  sendDeferred,
  sendRejected,
  sendStarted,
  sendSucceeded,
  type OutboxItem,
  type WithOutbox,
} from './outboxSlice';

type State = WithOutbox & WithSession;
type Effect = ListenerEffectAPI<State, AppDispatch>;

/** Delays between retries while the server stays unreachable. */
const BACKOFF_MS = [2_000, 5_000, 15_000, 30_000];
/** Codes meaning the attempt in the cache is stale and must be fetched again. */
const STALE_ATTEMPT = new Set(['attempt_closed', 'already_answered', 'attempt_outdated']);

let flushing = false;
let failures = 0;
let retryTimer: ReturnType<typeof setTimeout> | null = null;

async function send(item: OutboxItem, dispatch: AppDispatch): Promise<{ error?: unknown }> {
  if (item.kind === 'answer') {
    return dispatch(
      attemptsApi.endpoints.submitAnswer.initiate(
        {
          attemptId: item.attemptId,
          itemIndex: item.itemIndex,
          answer: item.answer,
          idempotencyKey: item.id,
          lang: item.lang,
        },
        { track: false },
      ),
    );
  }
  return dispatch(
    contentApi.endpoints.setPosition.initiate(
      { lessonId: item.lessonId, stepIndex: item.stepIndex },
      { track: false },
    ),
  );
}

function retryLater(effect: Effect): void {
  if (retryTimer) return;
  const delay = BACKOFF_MS[Math.min(failures, BACKOFF_MS.length - 1)];
  failures += 1;
  retryTimer = setTimeout(() => {
    retryTimer = null;
    effect.dispatch(flushRequested());
  }, delay);
}

/** Deliver queued items one at a time, in order, until the queue is empty or delivery stalls. */
async function flush(effect: Effect): Promise<void> {
  if (flushing) return;
  flushing = true;
  try {
    for (;;) {
      const state = effect.getState();
      const item = selectNextItem(state);
      if (!item || selectSessionStatus(state) !== 'authenticated') return;

      effect.dispatch(sendStarted(item.id));
      const { error } = await send(item, effect.dispatch);

      if (!error) {
        failures = 0;
        effect.dispatch(sendSucceeded(item.id));
      } else if (isTransientError(error) || errorStatus(error) === 401) {
        // Offline, server trouble, or signed out: keep the item for later.
        effect.dispatch(sendDeferred(item.id));
        if (errorStatus(error) !== 401) retryLater(effect);
        return;
      } else {
        const code = errorCode(error) ?? 'unknown';
        effect.dispatch(sendRejected({ id: item.id, code }));
        if (item.kind === 'answer' && STALE_ATTEMPT.has(code)) {
          effect.dispatch(baseApi.util.invalidateTags([{ type: 'Attempt', id: item.attemptId }]));
        }
      }
    }
  } finally {
    flushing = false;
  }
}

export const outboxListener = createListenerMiddleware<State, AppDispatch>();

outboxListener.startListening({
  // Signing in also triggers delivery of anything left from an earlier visit.
  matcher: isAnyOf(answerQueued, positionQueued, flushRequested, sessionStarted),
  effect: (_, effect) => flush(effect),
});

/** Retry as soon as the browser reports the network is back. */
export function flushWhenOnline(dispatch: AppDispatch): () => void {
  const onOnline = () => {
    failures = 0;
    dispatch(flushRequested());
  };
  window.addEventListener('online', onOnline);
  return () => window.removeEventListener('online', onOnline);
}
